import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

declare var google: any;

export interface LocationDetails {
  city: string;
  state: string;
  zipCode: string;
  country: string;
  latitude: number;
  longitude: number;
  timeZone: string;
  formattedAddress?: string;
  googlePlaceId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsService {
  private apiKey: string;
  private googleMapsLoaded: boolean = false;

  constructor(private http: HttpClient) {
    this.apiKey = environment.GOOGLE_API_KEY || '';
  }

  /**
   * Load Google Maps JavaScript API script dynamically
   */
  loadGoogleMapsScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.googleMapsLoaded || ((window as any).google && (window as any).google.maps && (window as any).google.maps.places)) {
        this.googleMapsLoaded = true;
        resolve();
        return;
      }

      // Create a callback function name
      const callbackName = 'googleMapsCallback_' + Date.now();
      (window as any)[callbackName] = () => {
        // Wait a bit to ensure Places library is fully initialized
        setTimeout(() => {
          if ((window as any).google && (window as any).google.maps && (window as any).google.maps.places) {
            this.googleMapsLoaded = true;
            delete (window as any)[callbackName];
            resolve();
          } else {
            delete (window as any)[callbackName];
            reject(new Error('Google Places library failed to load'));
          }
        }, 100);
      };

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places&loading=async&callback=${callbackName}`;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        delete (window as any)[callbackName];
        reject(new Error('Failed to load Google Maps script'));
      };
      document.head.appendChild(script);
    });
  }

  /**
   * Initialize Google Places Autocomplete
   * Note: Google recommends using PlaceAutocompleteElement for new implementations,
   * but Autocomplete is still supported and will continue to work.
   * See: https://developers.google.com/maps/documentation/javascript/places-migration-overview
   */
  initAutocomplete(inputElement: HTMLInputElement): any {
    if (!(window as any).google) {
      throw new Error('Google Maps API not loaded');
    }

    if (!(window as any).google.maps) {
      throw new Error('Google Maps API not fully loaded');
    }

    if (!(window as any).google.maps.places) {
      throw new Error('Google Places library not loaded');
    }

    // Using legacy Autocomplete API (still supported, deprecation warning is informational)
    return new (window as any).google.maps.places.Autocomplete(inputElement, {
      types: ['address']
    });
  }

  /**
   * Get location details from a place result
   */
  getLocationDetails(place: any): Promise<LocationDetails> {
    return new Promise((resolve, reject) => {
      if (!place) {
        reject(new Error('Place is required'));
        return;
      }

      const geometry = place.geometry;
      const addressComponents = place.address_components;
      const lat = geometry.location.lat();
      const lng = geometry.location.lng();

      // Extract address components
      let city = '';
      let state = '';
      let zipCode = '';
      let country = '';

      addressComponents.forEach((component: any) => {
        const types = component.types;

        if (types.includes('locality') || types.includes('sublocality')) {
          city = component.long_name;
        }

        if (types.includes('administrative_area_level_1')) {
          state = component.short_name;
        }

        if (types.includes('postal_code')) {
          zipCode = component.long_name;
        }

        if (types.includes('country')) {
          country = component.short_name;
        }
      });

      // Extract Google Place ID
      const googlePlaceId = place.place_id || '';

      // Get timezone
      this.getTimezone(lat, lng).subscribe(
        (timezone: string) => {
          const locationDetails: LocationDetails = {
            city: city || '',
            state: state || '',
            zipCode: zipCode || '',
            country: country || '',
            latitude: lat,
            longitude: lng,
            timeZone: timezone,
            formattedAddress: place.formatted_address,
            googlePlaceId: googlePlaceId
          };
          resolve(locationDetails);
        },
        (error) => {
          // Even if timezone fails, return other details
          const locationDetails: LocationDetails = {
            city: city || '',
            state: state || '',
            zipCode: zipCode || '',
            country: country || '',
            latitude: lat,
            longitude: lng,
            timeZone: '',
            formattedAddress: place.formatted_address,
            googlePlaceId: googlePlaceId
          };
          resolve(locationDetails);
        }
      );
    });
  }

  /**
   * Get timezone from latitude and longitude using Google Time Zone API
   */
  getTimezone(lat: number, lng: number): Observable<string> {
    const timestamp = Math.floor(Date.now() / 1000);
    const url = `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${this.apiKey}`;

    return this.http.get<any>(url).pipe(
      map((response) => {
        if (response.status === 'OK' && response.timeZoneId) {
          return response.timeZoneId;
        }
        return '';
      }),
      catchError((error) => {
        console.error('Error getting timezone:', error);
        return from(['']);
      })
    );
  }
}

